import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useConfig } from '../../config/ConfigContext';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet';

const Header = () => {
  const { cart } = useCart();
  const { config, isFeatureEnabled } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const itemCount = cart.items.reduce((total, item) => total + item.quantity, 0);

  // Check if we're on the home page
  const isHomePage = location.pathname === '/';

  // Scroll listener for navbar visibility (only on home page)
  useEffect(() => {
    if (!isHomePage) {
      setIsVisible(true); // Always visible on other pages
      return;
    }

    // Start hidden on home page
    setIsVisible(false);

    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);



  if (!config) return null;

  return (
    <header
      className={`sticky md:fixed top-0 left-0 right-0 z-50 bg-white shadow-md transition-all duration-500 ${isHomePage && !isVisible ? 'md:opacity-0 md:-translate-y-full md:pointer-events-none' : 'opacity-100 translate-y-0'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img
                src={config.branding.logo}
                alt={config.branding.companyName}
                className="h-7 md:h-9 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8 ml-8">
            <Link to="/" className="text-gray-700 hover:text-brand-blue font-medium transition-colors">
              Home
            </Link>

            {/* Shop Link (Standard - No Mega Menu) */}
            <Link to="/products" className="text-gray-700 hover:text-brand-blue font-medium transition-colors">
              Shop
            </Link>

            <Link to="/about" className="text-gray-700 hover:text-brand-blue font-medium transition-colors">
              About Us
            </Link>

            <Link to="/contact" className="text-gray-700 hover:text-brand-blue font-medium transition-colors">
              Contact
            </Link>
          </nav>

          {/* Spacer to push Search to the right */}
          <div className="flex-grow"></div>

          {/* Right Side Icons */}
          <div className="flex items-center space-x-6">

            {/* Cart */}
            <Link to="/cart" className="relative group">
              <div className="p-2 text-brand-blue hover:text-brand-red transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-brand-red text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </div>
            </Link>



            {/* Mobile Menu Drawer */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button className="md:hidden p-2 text-gray-600 hover:text-brand-blue">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                  <span className="sr-only">Open menu</span>
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[85vw] sm:w-[385px] overflow-y-auto">
                <SheetHeader className="text-left">
                  <SheetTitle asChild>
                    <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                      <img
                        src={config.branding.logo}
                        alt={config.branding.companyName}
                        className="h-8 w-auto"
                      />
                    </Link>
                  </SheetTitle>
                </SheetHeader>



                <nav className="flex flex-col gap-6 mt-8">
                  {/* Main Navigation Links */}
                  <div className="flex flex-col gap-3">
                    <Link
                      to="/"
                      className="text-gray-900 hover:text-brand-blue font-medium text-lg transition-colors py-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Home
                    </Link>
                    <Link
                      to="/products"
                      className="text-gray-900 hover:text-brand-blue font-medium text-lg transition-colors py-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      All Products
                    </Link>
                    <Link
                      to="/about"
                      className="text-gray-900 hover:text-brand-blue font-medium text-lg transition-colors py-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      About Us
                    </Link>
                    <Link
                      to="/contact"
                      className="text-gray-900 hover:text-brand-blue font-medium text-lg transition-colors py-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Contact
                    </Link>
                  </div>

                  {/* Shop by Category */}
                  <div className="pt-6 border-t border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                      Shop by Category
                    </h3>
                    <div className="flex flex-col gap-3">
                      <Link
                        to="/products?category=mattresses"
                        className="flex items-center gap-3 text-gray-900 hover:text-brand-blue hover:bg-blue-50 font-medium p-3 rounded-lg transition-all"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <div>
                          <div className="font-semibold">Mattresses</div>
                          <div className="text-xs text-gray-600">Premium comfort</div>
                        </div>
                      </Link>
                      <Link
                        to="/products?category=pillows"
                        className="flex items-center gap-3 text-gray-900 hover:text-brand-blue hover:bg-blue-50 font-medium p-3 rounded-lg transition-all"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <div>
                          <div className="font-semibold">Pillows</div>
                          <div className="text-xs text-gray-600">Soft & supportive</div>
                        </div>
                      </Link>
                      <Link
                        to="/products?category=sofa_beds"
                        className="flex items-center gap-3 text-gray-900 hover:text-brand-blue hover:bg-blue-50 font-medium p-3 rounded-lg transition-all"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <div>
                          <div className="font-semibold">Sofa Beds</div>
                          <div className="text-xs text-gray-600">Space-saving solutions</div>
                        </div>
                      </Link>
                      <Link
                        to="/products?category=sleep_solutions"
                        className="flex items-center gap-3 text-gray-900 hover:text-brand-blue hover:bg-blue-50 font-medium p-3 rounded-lg transition-all"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <div>
                          <div className="font-semibold">Sleep Solutions</div>
                          <div className="text-xs text-gray-600">Enhanced rest</div>
                        </div>
                      </Link>
                      <Link
                        to="/products?category=bedding_accessories"
                        className="flex items-center gap-3 text-gray-900 hover:text-brand-blue hover:bg-blue-50 font-medium p-3 rounded-lg transition-all"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <div>
                          <div className="font-semibold">Bedding Accessories</div>
                          <div className="text-xs text-gray-600">Complete your set</div>
                        </div>
                      </Link>
                    </div>
                  </div>


                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

    </header>
  );
};

export default Header;

